import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { systemPrefersReducedMotion, type MotionLevel } from '../lib/motion';
import type { GreetingMode } from '../lib/greeting';
import type { SplashVariant } from '../components/SessionSplash';

type MotionContextValue = {
  level: MotionLevel;
  setLevel: (level: MotionLevel) => void;
  greeting: GreetingMode;
  setGreeting: (mode: GreetingMode) => void;
  /** Which shape the "you started working" moment takes; 'off' skips it. */
  splash: SplashVariant | 'off';
  setSplash: (variant: SplashVariant | 'off') => void;
  /** Full choreography: ink, flights, the receipt, the greeting animation. */
  animates: boolean;
  /** View transitions between screens — the first thing to drop. */
  morphs: boolean;
};

const MotionContext = createContext<MotionContextValue | null>(null);

const LEVEL_KEY = 'whoworks:motion';
const GREETING_KEY = 'whoworks:greeting';
const SPLASH_KEY = 'whoworks:splash';

// Per device, not per account: motion is a property of the screen you are
// holding. A phone that stutters should not be fixed from a laptop.
const readLevel = (): MotionLevel => {
  const stored = localStorage.getItem(LEVEL_KEY);
  if (stored === 'full' || stored === 'reduced' || stored === 'off') {
    return stored;
  }
  return systemPrefersReducedMotion() ? 'off' : 'full';
};

const readGreeting = (): GreetingMode => {
  const stored = localStorage.getItem(GREETING_KEY);
  if (stored === 'parts' || stored === 'daily' || stored === 'off') {
    return stored;
  }
  return 'parts';
};

const readSplash = (): SplashVariant | 'off' => {
  const stored = localStorage.getItem(SPLASH_KEY);
  if (stored === 'wash' || stored === 'curtain' || stored === 'focus' || stored === 'off') {
    return stored;
  }
  // The wash is the moment the motion spec describes, so it is what someone
  // who never opens this setting gets.
  return 'wash';
};

export const MotionProvider = ({ children }: { children: React.ReactNode }) => {
  const [level, setLevelState] = useState<MotionLevel>(() => readLevel());
  const [greeting, setGreetingState] = useState<GreetingMode>(() => readGreeting());
  const [splash, setSplashState] = useState<SplashVariant | 'off'>(() => readSplash());

  // The class is what CSS keys off — view transitions and the ambient session
  // tint are pure CSS and never see the React context.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('motion-full', level === 'full');
    root.classList.toggle('motion-off', level === 'off');
  }, [level]);

  const value = useMemo<MotionContextValue>(
    () => ({
      level,
      greeting,
      splash,
      setLevel: (next) => {
        localStorage.setItem(LEVEL_KEY, next);
        setLevelState(next);
      },
      setGreeting: (next) => {
        localStorage.setItem(GREETING_KEY, next);
        setGreetingState(next);
      },
      setSplash: (next) => {
        localStorage.setItem(SPLASH_KEY, next);
        setSplashState(next);
      },
      animates: level === 'full',
      morphs: level === 'full',
    }),
    [level, greeting, splash],
  );

  return <MotionContext.Provider value={value}>{children}</MotionContext.Provider>;
};

export const useMotion = (): MotionContextValue => {
  const ctx = useContext(MotionContext);
  if (!ctx) {
    throw new Error('useMotion must be used within MotionProvider');
  }
  return ctx;
};
